import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { ITemplate } from "../types/template";
import { loadTemplates } from "../api/template";
import { useSocket } from "./SocketContext";

interface TemplatesContextType {
  templates: ITemplate[];
  loading: boolean;
  loadAllTemplates: () => Promise<void>;
}

const TemplatesContext = createContext<TemplatesContextType | null>(null);

export const TemplatesProvider = ({ children }: { children: ReactNode }) => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- LOAD ---------------- */
  const loadAllTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await loadTemplates();
      setTemplates(response.data ?? []);
    } catch (err) {
      console.error("[TemplatesContext] loadAllTemplates", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------------- SIGNALR (opcional futuro) ---------------- */
  const { subscribeEvent } = useSocket();

  React.useEffect(() => {
    const unsub = subscribeEvent<{ templates: ITemplate[] }>(
      "TemplatesUpdated",
      (data) => {
        console.log("[TemplatesContext] TemplatesUpdated", data.templates);
        setTemplates(data.templates);
      }
    );

    return () => unsub();
  }, [subscribeEvent]);

  const value = useMemo(
    () => ({
      templates,
      loading,
      loadAllTemplates,
    }),
    [templates, loading, loadAllTemplates]
  );

  return (
    <TemplatesContext.Provider value={value}>
      {children}
    </TemplatesContext.Provider>
  );
};

export const useTemplates = () => {
  const ctx = useContext(TemplatesContext);
  if (!ctx) {
    throw new Error("useTemplates must be used inside TemplatesProvider");
  }
  return ctx;
};
